import React, { useCallback, useEffect, useState } from "react";
import AdminSectionHeader from "@/components/admin/AdminSectionHeader";
import AdminTable from "@/components/admin/AdminTable";
import CTAButton from "@/components/app/CTAButton";
import AppModal from "@/components/app/AppModal";
import { StatusBadge } from "@/components/app/StatusBadge";
import { api, formatApiError } from "@/lib/api";
import { Trash2, Plus, Layers } from "lucide-react";

export default function AdminCategories() {
    const [tcg, setTcg] = useState(null);
    const [categories, setCategories] = useState([]);
    const [rarities, setRarities] = useState([]);
    const [rules, setRules] = useState([]);
    const [error, setError] = useState("");
    const [editing, setEditing] = useState(null);
    const [creating, setCreating] = useState(false);
    const [newCat, setNewCat] = useState({ slug: "", label: "", color: "#7E8B9C", order: 100 });
    const [newRule, setNewRule] = useState({ card_rarity_id: "", variant_type: "", mission_category_id: "", priority: 0 });

    const load = useCallback(async () => {
        try {
            const [tcgRes, catRes, rRes, rulesRes] = await Promise.all([
                api.get("/admin/catalog/tcgs"),
                api.get("/admin/catalog/mission-categories", { params: { tcg_slug: "pokemon" } }),
                api.get("/admin/catalog/rarities", { params: { tcg_slug: "pokemon" } }),
                api.get("/admin/catalog/category-rules", { params: { tcg_slug: "pokemon" } }),
            ]);
            setTcg(tcgRes.data.find((t) => t.slug === "pokemon"));
            setCategories(catRes.data);
            setRarities(rRes.data);
            setRules(rulesRes.data);
        } catch (e) { setError(formatApiError(e)); }
    }, []);

    useEffect(() => { load(); }, [load]);

    async function updateCat(c, patch) {
        try { await api.patch(`/admin/catalog/mission-categories/${c._id}`, patch); await load(); }
        catch (e) { setError(formatApiError(e)); }
    }

    async function deleteCat(c) {
        try { await api.delete(`/admin/catalog/mission-categories/${c._id}`); await load(); setEditing(null); }
        catch (e) { setError(formatApiError(e)); }
    }

    async function createCategory() {
        if (!tcg) return;
        try {
            await api.post("/admin/catalog/mission-categories", { ...newCat, tcg_id: tcg._id });
            setCreating(false);
            setNewCat({ slug: "", label: "", color: "#7E8B9C", order: 100 });
            await load();
        } catch (e) { setError(formatApiError(e)); }
    }

    async function createRule() {
        if (!tcg || !newRule.mission_category_id) return;
        try {
            await api.post("/admin/catalog/category-rules", {
                tcg_id: tcg._id,
                mission_category_id: newRule.mission_category_id,
                card_rarity_id: newRule.card_rarity_id || null,
                variant_type: newRule.variant_type || null,
                priority: newRule.priority,
            });
            setNewRule({ card_rarity_id: "", variant_type: "", mission_category_id: "", priority: 0 });
            await load();
        } catch (e) { setError(formatApiError(e)); }
    }

    async function deleteRule(r) {
        try { await api.delete(`/admin/catalog/category-rules/${r._id}`); await load(); }
        catch (e) { setError(formatApiError(e)); }
    }

    const rarityById = Object.fromEntries(rarities.map((r) => [r._id, r]));
    const catById = Object.fromEntries(categories.map((c) => [c._id, c]));

    return (
        <>
            <AdminSectionHeader
                title="Catégories mission"
                description="CardRarity = rareté source. MissionCategory = catégorie publique configurable. Mappées via règles."
                action={
                    <CTAButton size="sm" icon={<Plus className="h-3.5 w-3.5" />} onClick={() => setCreating(true)} testId="cat-create">
                        Nouvelle catégorie
                    </CTAButton>
                }
            />
            {error && <div className="mb-4 rounded-lg border border-md-danger/30 bg-md-danger/10 px-4 py-2 text-sm text-md-danger">{error}</div>}

            <div className="rounded-2xl border border-md-border bg-md-surface p-6">
                <p className="md-overline mb-4">Catégories Mission Dresseur</p>
                <AdminTable
                    emptyLabel="Aucune catégorie."
                    columns={[
                        { key: "order", label: "Ordre", render: (c) => <span className="font-mono text-xs">{c.order}</span> },
                        { key: "color", label: "", render: (c) => <span className="inline-block h-5 w-5 rounded-full border border-md-border" style={{ background: c.color }} /> },
                        { key: "label", label: "Label" },
                        { key: "slug", label: "Slug", render: (c) => <code className="text-xs">{c.slug}</code> },
                        { key: "is_active", label: "Actif", render: (c) => <StatusBadge tone={c.is_active ? "success" : "neutral"}>{c.is_active ? "actif" : "off"}</StatusBadge> },
                        { key: "is_visible", label: "Visible", render: (c) => <StatusBadge tone={c.is_visible ? "primary" : "neutral"}>{c.is_visible ? "visible" : "caché"}</StatusBadge> },
                        { key: "eligible_for_mission", label: "Mission", render: (c) => <StatusBadge tone={c.eligible_for_mission ? "gold" : "neutral"}>{c.eligible_for_mission ? "éligible" : "off"}</StatusBadge> },
                        { key: "actions", label: "", render: (c) => (
                            <div className="flex gap-2">
                                <CTAButton size="sm" variant="outline" onClick={() => setEditing(c)} testId={`cat-edit-${c.slug}`}>Modifier</CTAButton>
                            </div>
                        )},
                    ]}
                    rows={categories}
                />
            </div>

            <div className="mt-8 rounded-2xl border border-md-border bg-md-surface p-6">
                <p className="md-overline mb-4">Règles de résolution — Rareté × Variante → Catégorie</p>
                <div className="mb-5 grid grid-cols-1 gap-3 sm:grid-cols-5">
                    <select value={newRule.card_rarity_id} onChange={(e) => setNewRule((r) => ({ ...r, card_rarity_id: e.target.value }))} data-testid="rule-rarity" className="rounded-lg border border-md-border bg-md-bg px-3 py-2 text-sm">
                        <option value="">Rareté (wildcard si vide)</option>
                        {rarities.map((r) => <option key={r._id} value={r._id}>{r.label}</option>)}
                    </select>
                    <select value={newRule.variant_type} onChange={(e) => setNewRule((r) => ({ ...r, variant_type: e.target.value }))} data-testid="rule-variant" className="rounded-lg border border-md-border bg-md-bg px-3 py-2 text-sm">
                        <option value="">Variante (wildcard)</option>
                        <option value="normal">normal</option>
                        <option value="reverse">reverse</option>
                        <option value="holo">holo</option>
                        <option value="first_edition">first_edition</option>
                        <option value="promo">promo</option>
                    </select>
                    <select value={newRule.mission_category_id} onChange={(e) => setNewRule((r) => ({ ...r, mission_category_id: e.target.value }))} data-testid="rule-category" className="rounded-lg border border-md-border bg-md-bg px-3 py-2 text-sm">
                        <option value="">→ Catégorie cible *</option>
                        {categories.map((c) => <option key={c._id} value={c._id}>{c.label}</option>)}
                    </select>
                    <input type="number" placeholder="Priorité" value={newRule.priority} onChange={(e) => setNewRule((r) => ({ ...r, priority: Number(e.target.value) }))} data-testid="rule-priority" className="rounded-lg border border-md-border bg-md-bg px-3 py-2 text-sm" />
                    <CTAButton size="sm" onClick={createRule} disabled={!newRule.mission_category_id} testId="rule-create">Ajouter règle</CTAButton>
                </div>
                <AdminTable
                    emptyLabel="Aucune règle. Crées-en pour résoudre les cartes vers tes catégories."
                    columns={[
                        { key: "priority", label: "Priorité", render: (r) => <span className="font-mono text-xs">{r.priority}</span> },
                        { key: "rarity", label: "Rareté", render: (r) => r.card_rarity_id ? (rarityById[r.card_rarity_id]?.label || "?") : <span className="text-md-text-2 italic">toutes</span> },
                        { key: "variant", label: "Variante", render: (r) => r.variant_type ? <code className="text-xs">{r.variant_type}</code> : <span className="text-md-text-2 italic">toutes</span> },
                        { key: "category", label: "→ Catégorie", render: (r) => (
                            <span className="inline-flex items-center gap-2">
                                <span className="inline-block h-3 w-3 rounded-full" style={{ background: catById[r.mission_category_id]?.color || "#ccc" }} />
                                {catById[r.mission_category_id]?.label || "?"}
                            </span>
                        )},
                        { key: "actions", label: "", render: (r) => (
                            <CTAButton size="sm" variant="ghost" icon={<Trash2 className="h-3 w-3" />} onClick={() => deleteRule(r)} testId={`rule-delete-${r._id}`}>Suppr</CTAButton>
                        )},
                    ]}
                    rows={rules}
                />
            </div>

            <AppModal
                open={creating}
                onOpenChange={setCreating}
                title="Nouvelle catégorie mission"
                icon={<Layers className="h-5 w-5" />}
                primaryAction={{ label: "Créer", onClick: createCategory, disabled: !newCat.slug || !newCat.label }}
                secondaryAction={{ label: "Annuler", onClick: () => setCreating(false) }}
            >
                <div className="space-y-3">
                    <div>
                        <label className="md-overline mb-1 block">Slug</label>
                        <input value={newCat.slug} onChange={(e) => setNewCat((c) => ({ ...c, slug: e.target.value }))} data-testid="new-cat-slug" className="w-full rounded-lg border border-md-border bg-md-bg px-3 py-2 text-sm" />
                    </div>
                    <div>
                        <label className="md-overline mb-1 block">Label</label>
                        <input value={newCat.label} onChange={(e) => setNewCat((c) => ({ ...c, label: e.target.value }))} data-testid="new-cat-label" className="w-full rounded-lg border border-md-border bg-md-bg px-3 py-2 text-sm" />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <label className="md-overline mb-1 block">Couleur</label>
                            <input type="color" value={newCat.color} onChange={(e) => setNewCat((c) => ({ ...c, color: e.target.value }))} data-testid="new-cat-color" className="h-10 w-full rounded-lg border border-md-border bg-md-bg" />
                        </div>
                        <div>
                            <label className="md-overline mb-1 block">Ordre</label>
                            <input type="number" value={newCat.order} onChange={(e) => setNewCat((c) => ({ ...c, order: Number(e.target.value) }))} data-testid="new-cat-order" className="w-full rounded-lg border border-md-border bg-md-bg px-3 py-2 text-sm" />
                        </div>
                    </div>
                </div>
            </AppModal>

            <AppModal
                open={!!editing}
                onOpenChange={(o) => { if (!o) setEditing(null); }}
                title={editing ? `Modifier — ${editing.label}` : ""}
                primaryAction={editing ? { label: "Enregistrer", onClick: async () => { await updateCat(editing, { label: editing.label, color: editing.color, order: editing.order, is_active: editing.is_active, is_visible: editing.is_visible, eligible_for_mission: editing.eligible_for_mission }); setEditing(null); } } : null}
                secondaryAction={editing ? { label: "Supprimer", onClick: () => deleteCat(editing) } : null}
            >
                {editing && (
                    <div className="space-y-3">
                        <div><label className="md-overline mb-1 block">Label</label><input value={editing.label} onChange={(e) => setEditing({ ...editing, label: e.target.value })} className="w-full rounded-lg border border-md-border bg-md-bg px-3 py-2 text-sm" /></div>
                        <div className="grid grid-cols-2 gap-3">
                            <div><label className="md-overline mb-1 block">Couleur</label><input type="color" value={editing.color} onChange={(e) => setEditing({ ...editing, color: e.target.value })} className="h-10 w-full rounded-lg border border-md-border bg-md-bg" /></div>
                            <div><label className="md-overline mb-1 block">Ordre</label><input type="number" value={editing.order} onChange={(e) => setEditing({ ...editing, order: Number(e.target.value) })} className="w-full rounded-lg border border-md-border bg-md-bg px-3 py-2 text-sm" /></div>
                        </div>
                        <div className="grid grid-cols-3 gap-3">
                            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={editing.is_active} onChange={(e) => setEditing({ ...editing, is_active: e.target.checked })} /> Active</label>
                            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={editing.is_visible} onChange={(e) => setEditing({ ...editing, is_visible: e.target.checked })} /> Visible</label>
                            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={editing.eligible_for_mission} onChange={(e) => setEditing({ ...editing, eligible_for_mission: e.target.checked })} /> Mission</label>
                        </div>
                    </div>
                )}
            </AppModal>
        </>
    );
}
